import React from 'react';

interface SearchParametersDisplayProps {
  vectorQueryText: string[];
  contraryQueryText?: string[];
  maxResults?: number;
  maxPassagesPerDocument?: number;
  filters?: {
    court?: string;
    state?: string;
    judge?: string;
    categories?: string;
    cause?: string;
    city?: string;
    plaintiffs?: string;
    defendants?: string;
    filingDateFrom?: string;
    filingDateTo?: string;
  };
  showTimestamp?: boolean;
  timestamp?: string;
  attemptNumber?: number;
  orchestratorDecision?: string;
}

export const SearchParametersDisplay: React.FC<SearchParametersDisplayProps> = ({
  vectorQueryText,
  contraryQueryText,
  maxResults,
  maxPassagesPerDocument,
  filters,
  showTimestamp = false,
  timestamp,
  attemptNumber,
  orchestratorDecision
}) => {
  const filterBadges: Array<{ label: string; value: string }> = [];
  const hardFilters: Array<{ label: string; value: string }> = [];
  
  if (filters) {
    if (filters.court) filterBadges.push({ label: '⚖️ Court', value: filters.court });
    if (filters.state) hardFilters.push({ label: '📍 State', value: filters.state });
    if (filters.judge) filterBadges.push({ label: '👨‍⚖️ Judge', value: filters.judge });
    if (filters.categories) filterBadges.push({ label: '📂 Categories', value: filters.categories });
    if (filters.cause) filterBadges.push({ label: '⚡ Cause', value: filters.cause });
    if (filters.city) filterBadges.push({ label: '🏙️ City', value: filters.city });
    if (filters.plaintiffs) filterBadges.push({ label: '👤 Plaintiffs', value: filters.plaintiffs });
    if (filters.defendants) filterBadges.push({ label: '👥 Defendants', value: filters.defendants });
    
    if (filters.filingDateFrom || filters.filingDateTo) {
      let dateRange: string;
      if (filters.filingDateFrom && filters.filingDateTo) {
        dateRange = `${filters.filingDateFrom} to ${filters.filingDateTo}`;
      } else if (filters.filingDateFrom) {
        dateRange = `from ${filters.filingDateFrom}`;
      } else {
        dateRange = `until ${filters.filingDateTo}`;
      }
      hardFilters.push({ label: '📅 Filing Date Range', value: dateRange });
    }
  }

  const formatTimestamp = (ts: string) => {
    return new Date(ts).toLocaleTimeString();
  };

  const getSearchTypeLabel = () => {
    if (!orchestratorDecision) return null;
    
    return {
      text: 'Search with Analysis',
      tooltip: 'Opinions found and summarized by AI writer',
      className: 'bg-purple-100 text-purple-800 border-purple-300'
    };
  };

  const searchTypeLabel = getSearchTypeLabel();

  return (
    <div data-component="SearchParametersDisplay" className="space-y-2 text-sm">
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-medium text-gray-900">
          🔎 Search Parameters {attemptNumber && attemptNumber > 1 ? `(Attempt #${attemptNumber})` : ''}
        </h4>
        <div className="flex items-center gap-3">
          {searchTypeLabel && (
            <span 
              className={`px-3 py-1 rounded-full text-xs font-semibold border ${searchTypeLabel.className}`}
              title={searchTypeLabel.tooltip}
            >
              {searchTypeLabel.text}
            </span>
          )}
          {showTimestamp && timestamp && (
            <div className="text-xs text-gray-600">
              {formatTimestamp(timestamp)}
            </div>
          )}
        </div>
      </div>
      
      <div>
        <span className="font-medium text-gray-700">Query Parts:</span>
        <div className="mt-1 flex flex-wrap gap-2">
          {vectorQueryText.map((part: string, idx: number) => (
            <div 
              key={idx}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-100 text-blue-800 rounded-full text-sm font-medium"
            >
              <span className="text-xs font-bold bg-blue-200 px-1.5 py-0.5 rounded-full">
                {idx + 1}
              </span>
              <span>{part}</span>
            </div>
          ))}
        </div>
      </div>

      {contraryQueryText && contraryQueryText.length > 0 && (
        <div>
          <span className="font-medium text-gray-700">Contrary Query Parts:</span>
          <div className="mt-1 flex flex-wrap gap-2">
            {contraryQueryText.map((part: string, idx: number) => (
              <div 
                key={idx}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-100 text-red-800 rounded-full text-sm font-medium"
              >
                <span className="text-xs font-bold bg-red-200 px-1.5 py-0.5 rounded-full">
                  {idx + 1}
                </span>
                <span>{part}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {filterBadges.length > 0 && (
        <div>
          <span className="font-medium text-gray-700">Score Boosters:</span>
          <div className="mt-1 flex flex-wrap gap-2">
            {filterBadges.map((badge, idx) => (
              <span 
                key={idx}
                className="inline-flex items-center gap-1 px-2 py-1 bg-white rounded border border-indigo-200 text-xs"
              >
                <span className="font-medium">{badge.label}:</span>
                <span className="text-gray-700">{badge.value}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {hardFilters.length > 0 && (
        <div>
          <span className="font-medium text-gray-700">Filters:</span>
          <div className="mt-1 flex flex-wrap gap-2">
            {hardFilters.map((filter, idx) => (
              <span 
                key={idx}
                className="inline-flex items-center gap-1 px-2 py-1 bg-white rounded border border-indigo-200 text-xs"
              >
                <span className="font-medium">{filter.label}:</span>
                <span className="text-gray-700">{filter.value}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-4 text-xs text-gray-600">
        {maxResults && <span>Max Results: <strong>{maxResults}</strong></span>}
        {maxPassagesPerDocument && <span>Max Passages: <strong>{maxPassagesPerDocument}</strong></span>}
      </div>
    </div>
  );
};
